import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Flame, TrendingUp, MapPin, ArrowRight } from 'lucide-react';
import { tickerService } from '../../services/tickerService';

const REFRESH_MS = 5 * 60 * 1000;

const FlameRow = ({ count }) => (
  <span aria-hidden="true" className="inline-flex">
    {Array.from({ length: 5 }).map((_, i) => (
      <Flame
        key={i}
        className={`w-3.5 h-3.5 -ml-0.5 ${i < count ? 'text-orange-500 fill-orange-400' : 'text-gray-300'}`}
      />
    ))}
  </span>
);

const TickerPill = ({ entry }) => (
  <Link
    to={`/jobs?postcode=${encodeURIComponent(entry.postcode)}`}
    className="group inline-flex items-center gap-3 px-4 py-2 rounded-full bg-white/90 backdrop-blur border border-orange-200 hover:border-orange-400 hover:bg-white transition-all shadow-sm hover:shadow"
  >
    <span className="text-xs font-semibold text-gray-400 tabular-nums w-5">
      #{entry.rank}
    </span>
    <FlameRow count={entry.heat_score} />
    <span className="font-semibold text-gray-900 tabular-nums">{entry.postcode}</span>
    {entry.city && (
      <span className="text-sm text-gray-600 hidden sm:inline">
        {entry.city}{entry.state ? `, ${entry.state}` : ''}
      </span>
    )}
    <span className="text-xs font-medium text-orange-700 bg-orange-100 px-2.5 py-0.5 rounded-full whitespace-nowrap leading-none inline-flex items-center justify-center text-center">
      {entry.project_count ?? entry.request_count} project{(entry.project_count ?? entry.request_count) === 1 ? '' : 's'}
    </span>
    {entry.group_count > 0 && (
      <span className="text-xs font-medium text-emerald-700 bg-emerald-100 px-2.5 py-0.5 rounded-full whitespace-nowrap leading-none">
        {entry.group_count} group{entry.group_count === 1 ? '' : 's'}
      </span>
    )}
    <ArrowRight className="w-3.5 h-3.5 text-gray-400 group-hover:text-orange-500 group-hover:translate-x-0.5 transition-transform" />
  </Link>
);

const HotPostcodesTicker = () => {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const trackRef = useRef(null);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      try {
        const res = await tickerService.topPostcodes({ limit: 5, days: 7 });
        if (!cancelled) setData(res);
      } catch (e) {
        if (!cancelled) setError(e?.message || 'ticker error');
      }
    };

    load();
    const id = setInterval(load, REFRESH_MS);
    return () => { cancelled = true; clearInterval(id); };
  }, []);

  // While the first fetch is in flight, keep the strip's final height reserved
  // so the hero below it does not shift when the ticker content arrives
  // (Lighthouse layout-shifts / cls-culprits on the home page).
  if (!data && !error) {
    return (
      <section
        aria-hidden="true"
        className="relative py-3 min-h-[148px] overflow-hidden bg-gradient-to-r from-orange-50 via-amber-50 to-rose-50 border-y border-orange-100"
      >
        <div className="relative max-w-6xl mx-auto px-4 animate-pulse">
          <div className="h-5 w-56 bg-orange-100 rounded mb-4" />
          <div className="flex gap-3">
            {Array.from({ length: 4 }).map((_, i) => (
              <div key={i} className="h-9 w-48 bg-white/80 border border-orange-100 rounded-full" />
            ))}
          </div>
          <div className="mt-3 h-4 w-64 mx-auto bg-orange-100 rounded" />
        </div>
      </section>
    );
  }

  if (error || !data.top || data.top.length === 0) {
    return null;
  }

  // Duplicate the items so the marquee loops seamlessly.
  const items = [...data.top, ...data.top];

  return (
    <section className="relative py-3 overflow-hidden bg-gradient-to-r from-orange-50 via-amber-50 to-rose-50 border-y border-orange-100">
      {/* glow blobs */}
      <div className="absolute -top-12 -left-12 w-64 h-64 bg-orange-200/40 rounded-full blur-3xl pointer-events-none" />
      <div className="absolute -bottom-12 -right-12 w-64 h-64 bg-rose-200/40 rounded-full blur-3xl pointer-events-none" />

      <div className="relative max-w-6xl mx-auto px-4">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <div className="relative">
              <Flame className="w-5 h-5 text-orange-500 fill-orange-400" />
              <span className="absolute -top-1 -right-1 w-2 h-2 bg-red-500 rounded-full animate-ping" />
            </div>
            <h3 className="text-sm sm:text-base font-bold text-gray-900 tracking-tight">
              Hottest postcodes this week
            </h3>
            <span className="text-xs text-gray-500 hidden sm:inline">
              · last {data.window_days} days
            </span>
          </div>
          <Link
            to="/jobs"
            className="text-xs sm:text-sm font-medium text-orange-700 hover:text-orange-900 inline-flex items-center gap-1"
          >
            Browse all projects <ArrowRight className="w-3.5 h-3.5" />
          </Link>
        </div>

        <div
          ref={trackRef}
          className="ticker-scroll relative flex gap-3 overflow-x-auto overflow-y-hidden no-scrollbar snap-x snap-mandatory"
          style={{
            maskImage: 'linear-gradient(to right, transparent, black 4%, black 96%, transparent)',
            WebkitMaskImage: 'linear-gradient(to right, transparent, black 4%, black 96%, transparent)',
            WebkitOverflowScrolling: 'touch',
            scrollSnapType: 'x mandatory',
          }}
        >
          <div
            className="ticker-track flex gap-3 shrink-0"
            style={{ animation: `ticker-scroll ${Math.max(20, items.length * 6)}s linear infinite` }}
          >
            {items.map((entry, idx) => (
              <div key={`${entry.postcode}-${idx}`} className="snap-start shrink-0">
                <TickerPill entry={entry} />
              </div>
            ))}
          </div>
        </div>

        <div className="mt-3 flex items-center justify-center gap-2 text-xs text-gray-500">
          <TrendingUp className="w-3.5 h-3.5 text-emerald-600" />
          <span>Updated every 5 minutes</span>
          <span className="text-gray-300">·</span>
          <MapPin className="w-3.5 h-3.5" />
          <span>Tap a postcode to see open projects</span>
        </div>
      </div>

      <style>{`
        @keyframes ticker-scroll {
          0%   { transform: translateX(0); }
          100% { transform: translateX(-50%); }
        }
        /* Pause auto-scroll when user is hovering OR interacting with the strip
           (focus inside, active touch). Lets users browse at their own pace. */
        .ticker-scroll:hover .ticker-track,
        .ticker-scroll:focus-within .ticker-track,
        .ticker-scroll:active .ticker-track {
          animation-play-state: paused;
        }
        /* Hide the native scrollbar so the strip still looks clean while being
           horizontally swipeable / wheel-scrollable. */
        .no-scrollbar { scrollbar-width: none; -ms-overflow-style: none; }
        .no-scrollbar::-webkit-scrollbar { display: none; }
      `}</style>
    </section>
  );
};

export default HotPostcodesTicker;
